/**
 * Layered slideshow: Notch — autoplay and navigation controller.
 * Drives vendor layered-slideshow-component via its public select() API.
 */
class LayeredNotchController extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoplayTimer = null;
  /** @type {MutationObserver | null} */
  #tabObserver = null;
  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;
  /** @type {MutationObserver | null} */
  #dragObserver = null;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {{ autoplay: boolean; autoplaySpeed: number; pauseOnHover: boolean; reducedMotion: boolean; slideCount: number }} */
  #config = {
    autoplay: false,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    reducedMotion: false,
    slideCount: 0,
  };
  #activeIndex = 0;
  #isPaused = false;
  #isDragging = false;
  #isVisible = true;
  #progressRaf = null;

  async connectedCallback() {
    if (customElements.get('layered-slideshow-component')) {
      await customElements.whenDefined('layered-slideshow-component');
    }

    this.#slideshow = this.querySelector('layered-slideshow-component');
    if (!this.#slideshow) return;

    this.#tabs = () =>
      Array.from(this.#slideshow.querySelectorAll('[role="tab"]'));
    this.#panels = () =>
      Array.from(this.#slideshow.querySelectorAll('[role="tabpanel"]'));
    this.#container = () =>
      this.#slideshow.querySelector('.layered-slideshow__container');

    this.#readConfig();
    this.#bindControls();
    this.#observeTabs();
    this.#observeDrag();
    this.#observeVisibility();
    this.#syncFromDom();
    this.#scheduleAutoplay();
  }

  disconnectedCallback() {
    this.#clearAutoplay();
    this.#tabObserver?.disconnect();
    this.#dragObserver?.disconnect();
    this.#viewportObserver?.disconnect();
    this.#abort?.abort();
    if (this.#progressRaf) cancelAnimationFrame(this.#progressRaf);
  }

  #slideshow;
  #tabs;
  #panels;
  #container;

  #readConfig() {
    const d = this.dataset;
    this.#config = {
      autoplay: d.autoplay === 'true',
      autoplaySpeed: (parseFloat(d.autoplaySpeed) || 5) * 1000,
      pauseOnHover: d.pauseOnHover !== 'false',
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      slideCount: parseInt(d.slideCount, 10) || this.#tabs().length,
    };
  }

  #bindControls() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const prev = this.querySelector('[data-notch-action="previous"]');
    const next = this.querySelector('[data-notch-action="next"]');
    prev?.addEventListener('click', () => this.#goRelative(-1), { signal });
    next?.addEventListener('click', () => this.#goRelative(1), { signal });

    this.querySelectorAll('[data-notch-dot]').forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const index = parseInt(dot.getAttribute('data-notch-dot'), 10);
          if (!Number.isNaN(index)) this.#goTo(index);
        },
        { signal }
      );
    });

    if (this.#config.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.#pauseAutoplay(), { signal });
      this.addEventListener('mouseleave', () => this.#resumeAutoplay(), { signal });
      this.addEventListener('focusin', () => this.#pauseAutoplay(), { signal });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.#resumeAutoplay();
      }, { signal });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#pauseAutoplay();
      else this.#resumeAutoplay();
    }, { signal });
  }

  #observeTabs() {
    const tablist = this.#slideshow.querySelector('[role="tablist"]');
    if (!tablist) return;

    this.#tabObserver = new MutationObserver(() => this.#syncFromDom());
    this.#tabObserver.observe(tablist, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-selected'],
    });
  }

  #observeDrag() {
    const container = this.#container();
    if (!container) return;

    this.#dragObserver = new MutationObserver(() => {
      this.#isDragging = container.hasAttribute('data-dragging');
      if (this.#isDragging) this.#pauseAutoplay();
      else this.#resumeAutoplay();
    });
    this.#dragObserver.observe(container, {
      attributes: true,
      attributeFilter: ['data-dragging'],
    });
  }

  #observeVisibility() {
    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        this.#isVisible = entries[0]?.isIntersecting ?? true;
        if (this.#isVisible) this.#resumeAutoplay();
        else this.#pauseAutoplay();
      },
      { threshold: 0.25 }
    );
    this.#viewportObserver.observe(this);
  }

  #getActiveIndex() {
    const tabs = this.#tabs();
    const i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    return i >= 0 ? i : 0;
  }

  #syncFromDom() {
    const prev = this.#activeIndex;
    this.#activeIndex = this.#getActiveIndex();
    this.#updateControlsUi();
    if (prev !== this.#activeIndex) {
      this.#scheduleAutoplay();
    }
  }

  #goTo(index) {
    const count = this.#tabs().length;
    if (count <= 1) return;
    const clamped = ((index % count) + count) % count;
    this.#slideshow.select?.(clamped);
    this.#activeIndex = clamped;
    this.#updateControlsUi();
    this.#scheduleAutoplay();
  }

  #goRelative(delta) {
    this.#goTo(this.#getActiveIndex() + delta);
  }

  #getSlideDuration() {
    const panels = this.#panels();
    const panel = panels[this.#getActiveIndex()];
    const custom = parseFloat(panel?.dataset.slideDuration || '0');
    if (custom > 0) return custom * 1000;
    return this.#config.autoplaySpeed;
  }

  #canAutoplay() {
    return (
      this.#config.autoplay &&
      !this.#config.reducedMotion &&
      this.#tabs().length > 1 &&
      !this.#isPaused &&
      !this.#isDragging &&
      this.#isVisible &&
      !document.hidden
    );
  }

  #clearAutoplay() {
    if (this.#autoplayTimer) {
      clearTimeout(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #scheduleAutoplay() {
    this.#clearAutoplay();
    this.#restartProgressAnimation();

    if (!this.#canAutoplay()) return;

    const duration = this.#getSlideDuration();
    this.#autoplayTimer = setTimeout(() => {
      const next = (this.#getActiveIndex() + 1) % this.#tabs().length;
      this.#goTo(next);
    }, duration);
  }

  #pauseAutoplay() {
    this.#isPaused = true;
    this.#clearAutoplay();
    this.#pauseProgressAnimation();
  }

  #resumeAutoplay() {
    this.#isPaused = false;
    this.#scheduleAutoplay();
  }

  #updateControlsUi() {
    const index = this.#getActiveIndex();
    const count = this.#tabs().length;

    const prev = this.querySelector('[data-notch-action="previous"]');
    const next = this.querySelector('[data-notch-action="next"]');
    if (prev) prev.disabled = count <= 1;
    if (next) next.disabled = count <= 1;

    this.querySelectorAll('[data-notch-dot]').forEach((dot) => {
      const dotIndex = parseInt(dot.getAttribute('data-notch-dot'), 10);
      const isActive = dotIndex === index;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      dot.classList.toggle('is-active', isActive);
    });

    const counter = this.querySelector('[data-notch-counter]');
    if (counter) {
      counter.textContent = `${index + 1} / ${count}`;
    }

    this.#restartProgressAnimation();
  }

  #restartProgressAnimation() {
    const activeDot = this.querySelector('[data-notch-dot].is-active');
    if (!activeDot || !this.#config.autoplay) return;

    const fill = activeDot.querySelector('.layered-notch-dot__progress');
    if (!fill) return;

    fill.style.animation = 'none';
    // force reflow
    void fill.offsetWidth;

    if (this.#canAutoplay()) {
      const duration = this.#getSlideDuration() / 1000;
      fill.style.animation = `layered-notch-progress ${duration}s linear forwards`;
    }
  }

  #pauseProgressAnimation() {
    const fill = this.querySelector('[data-notch-dot].is-active .layered-notch-dot__progress');
    if (fill) {
      fill.style.animationPlayState = 'paused';
    }
  }
}

if (!customElements.get('layered-notch-controller')) {
  customElements.define('layered-notch-controller', LayeredNotchController);
}
